import * as React from 'react';

import { useFixedBodyLock } from '@hooks/useFixedBodyLock';
import { cn } from '@lib/utils';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  KEYBOARD_SHEET_MAX_HEIGHT,
} from './drawer';
import { Button } from './button';
import { CAPPED_SHEET_BODY_CLASS, scrollFocusedIntoView } from './cappedSheet';
import { HELD_BUTTON_CLASS, ignoreRepeatedEnter } from './heldButton';

export interface ConfirmActionDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  title: string;
  description?: React.ReactNode;

  confirmLabel?: string;
  cancelLabel?: string;
  confirmingLabel?: string;

  /** Button variant for the confirm action. Defaults to 'destructive'. */
  variant?: 'destructive' | 'default';
  /**
   * Which button is the solid one. 'confirm' (default): the confirm button carries `variant`.
   * 'cancel': the cancel button is solid teal and the confirm button outlined (`variant` is
   * ignored), for confirmations where staying is the expected answer (e.g. un-Going an event).
   */
  emphasis?: 'confirm' | 'cancel';

  isConfirming?: boolean;
  /**
   * Holds the confirm button (aria-disabled, the press ignored) while what the drawer holds is not
   * ready to send, such as a half-picked date. Held rather than disabled, as while confirming.
   */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  /** Called when cancel button is clicked. Falls back to onOpenChange(false) if not provided. */
  onCancel?: () => void;

  /**
   * What the drawer holds between its title and its buttons, such as what a change will do and a
   * note field. Given, it scrolls there, and the drawer is a keyboard sheet. Left out, nothing
   * renders there.
   */
  children?: React.ReactNode;

  /**
   * Where focus goes once the drawer has closed: call `event.preventDefault()` and focus it.
   * `opener` is the control that had focus as the drawer opened, while it is still on the page,
   * else null. Left alone, a drawer with content gives focus back to it; one without leaves focus
   * where it was all along, since it never moved focus off the opener.
   */
  onCloseAutoFocus?: (event: Event, opener: HTMLElement | null) => void;
}

export const ConfirmActionDrawer: React.FC<ConfirmActionDrawerProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmingLabel,
  variant = 'destructive',
  emphasis = 'confirm',
  isConfirming = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  children,
  onCloseAutoFocus,
}) => {
  // Both buttons are 44px tall, the touch floor, whichever one is solid.
  const buttonClass = 'h-11 rounded-xl';
  // The brand teal with an ink label: 8.37:1 at rest and 5.84:1 hovered. White on this teal is
  // 2.08:1, so labels on it are ink.
  const solidTealClass = 'h-11 rounded-xl bg-mb-teal hover:bg-mb-teal-dark text-mb-ink';
  const cancelEmphasised = emphasis === 'cancel';
  const confirmSolid = !cancelEmphasised && variant === 'default';
  // Content (a note field, say) makes this a keyboard drawer, so it takes the house keyboard sheet
  // recipe: the sheet is capped, so its top stays on screen while vaul lifts it for the keyboard,
  // the page under it is pinned, and the content scrolls between the title and the buttons. Where
  // even the title and the buttons don't fit under the cap (a phone on its side at a large text
  // size), everything under the handle scrolls, so the buttons can still be reached. Without
  // content it is the drawer it always was.
  const hasContent = Boolean(children);
  useFixedBodyLock(open && hasContent);

  // The control that had focus as the drawer opened, to give focus back to. Every one of these
  // drawers opens from state, with no Trigger, and Radix's modal dialog hands focus back to its
  // Trigger alone.
  const openerRef = React.useRef<HTMLElement | null>(null);

  /**
   * Radix's open autofocus, which vaul turns off: focus stayed on the page under the sheet, where
   * Tab walked the controls behind the scrim and a screen reader was not told a dialog opened. A
   * drawer with content takes focus onto the sheet itself (its focus scope, tabIndex -1), as the
   * celebration frame does: not a control in it, so no keyboard opens on a phone.
   */
  const handleOpenAutoFocus = (event: Event) => {
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    if (!hasContent) return;
    event.preventDefault();
    const sheet = event.currentTarget;
    if (sheet instanceof HTMLElement) sheet.focus({ preventScroll: true });
  };

  /** Radix's close autofocus: the page's choice, else, for a drawer with content, the opener. */
  const handleCloseAutoFocus = (event: Event) => {
    const recorded = openerRef.current;
    openerRef.current = null;
    const opener = recorded !== null && recorded.isConnected ? recorded : null;
    onCloseAutoFocus?.(event, opener);
    if (event.defaultPrevented || !hasContent) return;
    event.preventDefault();
    opener?.focus({ preventScroll: true });
  };

  const sheetBody = (
    <>
      <DrawerHeader className={cn('px-0 text-left', hasContent && 'shrink-0')}>
        <DrawerTitle>{title}</DrawerTitle>
        {/* A description can name a value, such as an address: it wraps rather than overflow. */}
        {description ? (
          <DrawerDescription className="wrap-anywhere">{description}</DrawerDescription>
        ) : null}
      </DrawerHeader>

      {/* The inset keeps a field's focus ring, and the gap before it, inside the scroller. Its
          floor keeps two lines of what it holds in view, and it hands a swipe at its end on to
          the scroller under the handle (CAPPED_SHEET_BODY_CLASS's docblock). */}
      {hasContent ? (
        <div className="-mx-1 flex min-h-12 flex-col gap-3 overflow-y-auto px-1 py-1">
          {children}
        </div>
      ) : null}

      {/* While it confirms, both buttons hold rather than turn disabled: a browser takes focus off
          a button that turns disabled, and in an open dialog it falls to the page under it, for
          as long as a save waits for a connection. The drawer itself still closes. Confirm keeps
          focus through a refusal, so it takes a held Enter as one press. */}
      <DrawerFooter className={cn('px-0', hasContent && 'shrink-0')}>
        <Button
          type="button"
          variant={cancelEmphasised ? 'default' : 'outline'}
          onClick={() => {
            if (isConfirming) return;
            if (onCancel) {
              onCancel();
            } else {
              onOpenChange(false);
            }
          }}
          aria-disabled={isConfirming}
          className={cn(cancelEmphasised ? solidTealClass : buttonClass, HELD_BUTTON_CLASS)}
        >
          {cancelLabel}
        </Button>
        <Button
          type="button"
          variant={cancelEmphasised ? 'outline' : variant}
          onClick={() => {
            if (isConfirming || confirmDisabled) return;
            onConfirm();
          }}
          onKeyDown={ignoreRepeatedEnter}
          aria-disabled={isConfirming || confirmDisabled}
          className={cn(confirmSolid ? solidTealClass : buttonClass, HELD_BUTTON_CLASS)}
        >
          {isConfirming ? (confirmingLabel ?? `${confirmLabel}…`) : confirmLabel}
        </Button>
      </DrawerFooter>
    </>
  );

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        // Focused from code as it opens: no ring on the sheet itself; its controls keep theirs.
        // AIDEV-NOTE: KEYBOARD_SHEET_MAX_HEIGHT is read only behind hasContent: older suites mock ./drawer without it
        className={cn('px-4 pb-safe', hasContent && `${KEYBOARD_SHEET_MAX_HEIGHT} outline-hidden`)}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        {hasContent ? (
          <div className={CAPPED_SHEET_BODY_CLASS} onFocus={scrollFocusedIntoView}>
            {sheetBody}
          </div>
        ) : (
          sheetBody
        )}
      </DrawerContent>
    </Drawer>
  );
};
